import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';
import { Contact, ContactGroup, ContactPayload } from '@app/store/contacts/contacts.types';
import { FloatingInputComponent } from '@app/framework/inputs/floating-input/floating-input.component';
import { DropdownComponent } from '@app/framework/inputs/dropdown/dropdown.component';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import { ContactsStore } from '@app/store/contacts/contacts.store';
import { ContactsApiService } from '@app/services/contacts-api.service';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { US_STATES } from '@app/framework/constants/states.constants';
import { CurrentUserService } from '@app/services/current-user.service';

@Component({
  selector: 'app-contact-sidebar',
  imports: [
    SidebarHeaderComponent,
    FormsModule,
    FloatingInputComponent,
    DropdownComponent,
    SimpleButtonComponent,
  ],
  templateUrl: './contact-sidebar.component.html',
  styleUrl: './contact-sidebar.component.scss',
})
export class ContactSidebarComponent implements OnInit {
  contactsStore = inject(ContactsStore);
  contactsApi = inject(ContactsApiService);
  contact = input<Contact>();
  contactGroup = input<ContactGroup>();
  interactionBarService = inject(InteractionBarStateService);
  userService = inject(CurrentUserService);

  form = viewChild('form', { read: NgForm });
  addCompanyInput = viewChild('addCompanyInput', { read: FloatingInputComponent });

  contactGroups = this.contactsStore.contactGroups;
  contactGroupOptions = computed(() => {
    return this.contactGroups().map((contactGroup) => {
      return { label: contactGroup.name, value: contactGroup.id };
    });
  });

  US_STATES = signal(US_STATES).asReadonly();
  states = computed(() => {
    return this.US_STATES().map((state) => {
      return { label: state.name, value: state.abbreviation };
    });
  });

  model: WritableSignal<ContactPayload> = signal({
    id: null,
    group_id: null,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    title: '',
    address: '',
    city: '',
    zip: '',
    state: '',
  });

  isAddCompany = signal(false);
  companyName = signal('');

  ngOnInit() {
    this.interactionBarService.setDisableClose(true);
    if (this.contact()) {
      this.model.set({ ...this.contact(), group_id: this.contactGroup()?.id });
    }
  }

  save() {
    this.form().control.markAllAsTouched();
    if (this.form().invalid) {
      return;
    }
    // consider refactoring the phone type in floating input component to return numbers only
    this.model.update((value) => ({
      ...value,
      phone: value?.phone?.replace(/[^\d+]*/g, '') ?? '',
    }));

    if (this.isAddCompany()) {
      this.model.update((value) => ({
        ...value,
        new_company_name: this.companyName(),
      }));
    }

    if (this.model().id) {
      this.contactsStore.updateContact(this.model(), this.isAddCompany());
    } else {
      this.contactsStore.createContact(this.model(), this.isAddCompany());
    }
    this.userService.refreshAccessibleUsers();
  }

  switchToAddCompany() {
    this.isAddCompany.set(true);
    setTimeout(() => {
      // setTimeout to save the day
      // it is needed to wait for the input to be visible
      this.addCompanyInput()?.focusInput();
    });
  }
}
